import type { ProjectType } from '@jira/platform__project-types/src/project-types.tsx';
import { fg } from '@atlassian/jira-feature-gating';

/**
 * The JSM `servicedeskPractices` values this redirect understands, plus a `string` fallback so the
 * Relay-generated `JiraServiceManagementPractice` enum (which carries a `%future added value`
 * member) is assignable without re-declaring that member — declaring it here is banned by
 * `@atlassian/relay/no-future-added-value`, and importing the generated enum would create a
 * cross-domain package dependency. Any unrecognised value falls through to the service request route.
 */
type JsmPractice =
	| 'CASE_MANAGEMENT'
	| 'CHANGE_MANAGEMENT'
	| 'DEVELOPER_ESCALATION'
	| 'INCIDENT_MANAGEMENT'
	| 'POST_INCIDENT_REVIEW'
	| 'PROBLEM_MANAGEMENT'
	| 'SERVICE_REQUEST'
	| (string & {});

type GetWorkItemRedirectionPathOptions = {
	issueKey: string | undefined;
	projectType: ProjectType | undefined;
	searchQuery: string;
	userPreference?: boolean;
	isEmbed?: boolean;
	wivEnabledForProject?: boolean;
	jsmPractice?: JsmPractice;
};

/**
 * Resolves work-item vs JSM redirect from project type, issue key, and current search string.
 *
 * For JSM (service_desk) issues, when the issue's `jsmPractice` is known and the view isn't embedded,
 * the redirect targets the practice-specific route (incidents / service requests); otherwise it
 * falls back to the generic JSM work item route.
 */
export function getWorkItemRedirectionPath({
	issueKey,
	projectType,
	searchQuery,
	userPreference = true,
	isEmbed = false,
	wivEnabledForProject = true,
	jsmPractice,
}: GetWorkItemRedirectionPathOptions): string | undefined {
	if (!issueKey || !projectType || searchQuery.includes('disableRedirect=true')) {
		return;
	}

	if (['software', 'business'].includes(projectType) && userPreference && wivEnabledForProject) {
		const embedPath = isEmbed ? '/embed' : '';
		return `/jira/browse/${issueKey}${embedPath}${searchQuery}`;
	}

	if (
		// eslint-disable-next-line jira/ff/no-preconditioning
		projectType === 'service_desk' &&
		fg('jsm_aniv_routing_phase_1') &&
		fg('jsm_aniv_route_redirection')
	) {
		// Practice-specific routes don't have an embed variant, so embedded views — and
		// requests where the practice couldn't be resolved — keep using the generic JSM
		// work item route, which classifies the practice itself before rendering.
		// JAA-4468: follow-up to add embed variants for the practice-specific routes.
		if (!isEmbed && jsmPractice) {
			// `jsmPractice` is the `servicedeskPractices` enum value. Incidents get a dedicated
			// route; every other practice (and any unrecognised/future value) uses the service
			// request route.
			const practiceSegment =
				jsmPractice === 'INCIDENT_MANAGEMENT' ? 'incidents' : 'service-requests';
			return `/jira/servicedesk/${practiceSegment}/${issueKey}${searchQuery}`;
		}

		const embedPath = isEmbed ? '/embed' : '';
		return `/browse/jsm-work-item/${issueKey}${embedPath}${searchQuery}`;
	}
}
